//#region src/bindings.ts
const bindings = {
	agentMemory: (options) => ({
		type: "agent-memory",
		...options
	}),
	ai: (options) => ({
		type: "ai",
		...options
	}),
	aiSearch: (options) => ({
		type: "ai-search",
		...options
	}),
	aiSearchNamespace: (options) => ({
		type: "ai-search-namespace",
		...options
	}),
	analyticsEngineDataset: (options) => ({
		type: "analytics-engine-dataset",
		...options
	}),
	artifacts: (options) => ({
		type: "artifacts",
		...options
	}),
	assets: () => ({ type: "assets" }),
	browser: (options) => ({
		type: "browser",
		...options
	}),
	d1: (options) => ({
		type: "d1",
		...options
	}),
	dispatchNamespace: (options) => ({
		type: "dispatch-namespace",
		...options
	}),
	durableObject: (options) => ({
		type: "durable-object",
		...options
	}),
	flagship: (options) => ({
		type: "flagship",
		...options
	}),
	hyperdrive: (options) => ({
		type: "hyperdrive",
		...options
	}),
	images: (options) => ({
		type: "images",
		...options
	}),
	json: (value) => ({
		type: "json",
		value
	}),
	kv: (options) => ({
		type: "kv",
		...options
	}),
	logfwdr: (options) => ({
		type: "logfwdr",
		...options
	}),
	media: (options) => ({
		type: "media",
		...options
	}),
	mtlsCertificate: (options) => ({
		type: "mtls-certificate",
		...options
	}),
	pipeline: (options) => ({
		type: "pipeline",
		...options
	}),
	queue: (options) => ({
		type: "queue",
		...options
	}),
	rateLimit: (options) => ({
		type: "rate-limit",
		...options
	}),
	r2: (options) => ({
		type: "r2",
		...options
	}),
	secret: () => ({ type: "secret" }),
	secretsStoreSecret: (options) => ({
		type: "secrets-store-secret",
		...options
	}),
	sendEmail: (options) => ({
		type: "send-email",
		...options
	}),
	stream: (options) => ({
		type: "stream",
		...options
	}),
	text: (value) => ({
		type: "text",
		value
	}),
	vectorize: (options) => ({
		type: "vectorize",
		...options
	}),
	versionMetadata: () => ({ type: "version-metadata" }),
	vpcService: (options) => ({
		type: "vpc-service",
		...options
	}),
	vpcNetwork: (options) => ({
		type: "vpc-network",
		...options
	}),
	webSearch: (options) => ({
		type: "web-search",
		...options
	}),
	worker: (options) => ({
		type: "worker",
		...options
	}),
	workerLoader: () => ({ type: "worker-loader" })
};

//#endregion
//#region src/triggers.ts
/**
* Triggers builder for configuring event triggers.
*
* @example
* ```typescript
* import { defineWorker, triggers } from "@cloudflare/config";
*
* export default defineWorker({
*   triggers: [
*     triggers.fetch({ pattern: "example.com/*", zone: "example.com" }),
*     triggers.queue({ name: "my-queue" }),
*     triggers.scheduled({ schedule: "0 * * * *" }),
*     triggers.scheduled({ schedule: "30 0 * * *" }),
*     triggers.email({ addresses: ["support@example.com"] }),
*   ],
* });
* ```
*/
const triggers = {
	fetch: (options) => ({
		type: "fetch",
		...options
	}),
	queue: (options) => ({
		type: "queue",
		...options
	}),
	scheduled: (options) => ({
		type: "scheduled",
		...options
	}),
	email: (options) => ({
		type: "email",
		...options
	})
};

//#endregion
//#region src/exports.ts
function durableObject(options) {
	return {
		type: "durable-object",
		...options
	};
}
function worker(options = {}) {
	return {
		type: "worker",
		...options
	};
}
/**
* Exports builder for configuring Worker exports.
*
* @example
* ```typescript
* import { defineWorker, exports } from "@cloudflare/config";
*
* export default defineWorker({
*   exports: {
*     MyDurableObject: exports.durableObject({ storage: "sqlite" }),
*     OldClass:        exports.durableObject({ state: "deleted" }),
*     OldName:         exports.durableObject({ state: "renamed", renamedTo: "NewName" }),
*     Outgoing:        exports.durableObject({ state: "transferred", transferredTo: "target-worker" }),
*     Incoming:        exports.durableObject({ state: "expecting-transfer", storage: "sqlite", transferFrom: "source-worker" }),
*   },
* });
* ```
*/
const exports = {
	durableObject,
	worker
};

//#endregion
//#region src/definition.ts
const DEFINITION = Symbol.for("@cloudflare/config:definition");
/**
* Unwrap an authored config from its value / promise / function shape, awaiting
* the result. A function config is invoked with {@link ConfigContext}.
*/
async function unwrap(config, ctx) {
	return typeof config === "function" ? await config(ctx) : await config;
}
/**
* Resolve any `cloudflare.config.ts` export to its plain config value.
*
* A `define*` helper stores its authored config plus `type` under the
* {@link DEFINITION} symbol; here we unwrap the config and stamp `type` back on.
* Every other export — a raw object/promise/function — is unwrapped as-is and
* already carries its own `type`. Discrimination happens afterwards via `type`.
*/
async function resolveExportDefinition(def, ctx) {
	if (typeof def === "object" && def !== null && DEFINITION in def) {
		const { config, type } = def[DEFINITION];
		return {
			...await unwrap(config, ctx),
			type
		};
	}
	return await unwrap(def, ctx);
}

//#endregion
//#region src/worker-definition.ts
function defineWorker(config) {
	return {
		[DEFINITION]: {
			config,
			type: "worker"
		},
		durableObject(options) {
			return {
				type: "durable-object",
				...options
			};
		},
		worker(options) {
			return {
				type: "worker",
				...options
			};
		}
	};
}

//#endregion
//#region src/settings-definition.ts
/**
* Declare shared settings.
* Authored as a named `settings` export.
*/
function defineSettings(config) {
	return { [DEFINITION]: {
		config,
		type: "settings"
	} };
}

//#endregion
export { triggers as a, exports as i, defineWorker as n, bindings as o, resolveExportDefinition as r, defineSettings as t };